import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './page.module.css';
import {
    MiniFormularioCampo,
    validarFormulario,
    ErrosValidacao,
} from '@tera/shared/src/lib/miniFormularioValidacao';
import { CHAVE_FORMULARIO } from '@tera/shared/src/lib/validadorFluxo';
import CampoAgendamento from './CampoAgendamento';
import CampoArquivo from './CampoArquivo';
import CampoCarrinho from './CampoCarrinho';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoRadio from '@/components/Formulario/CampoRadio';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoData from '@/components/Formulario/CampoData';
import CampoTelefone from '@/components/Formulario/CampoTelefone';
import CampoMascara from '@/components/Formulario/CampoMascara';

type Estado =
    | 'carregando'
    | 'pendente'
    | 'preenchido'
    | 'cancelado'
    | 'expirado'
    | 'invalido'
    | 'erro';

type Definicao = {
    nome?: string;
    descricao?: string;
    texto_botao?: string;
    campos: MiniFormularioCampo[];
};

const FormularioPublico = () => {
    const { token } = useParams<{ token: string }>();
    const [estado, setEstado] = useState<Estado>('carregando');
    const [definicao, setDefinicao] = useState<Definicao | null>(null);
    const [valores, setValores] = useState<Record<string, any>>({});
    const [erros, setErros] = useState<ErrosValidacao>({});
    const [enviando, setEnviando] = useState(false);
    const [cancelando, setCancelando] = useState(false);
    // Rótulo do botão de desistir, definido no nó do fluxo. Vazio = sem botão.
    const [cancelarLabel, setCancelarLabel] = useState<string | null>(null);
    const [voltarUrl, setVoltarUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                const res = await axios.get(`/api/formulario-publico/${token}`);
                if (cancelado) return;
                const data = res.data;
                if (data.estado === 'pendente') {
                    setDefinicao(data.formulario);
                    setValores(data.valores ?? {});
                    setCancelarLabel(data.cancelarLabel ?? null);
                    setEstado('pendente');
                } else {
                    setEstado(data.estado);
                }
            } catch (err: any) {
                if (cancelado) return;
                setEstado(err.response?.status === 404 ? 'invalido' : 'erro');
            }
        })();
        return () => {
            cancelado = true;
        };
    }, [token]);

    const setValor = (nome: string, valor: any) => {
        setValores((v) => ({ ...v, [nome]: valor }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!definicao) return;

        const errosLocais = validarFormulario(definicao.campos, valores);
        setErros(errosLocais);
        if (Object.keys(errosLocais).length > 0) return;

        // Campos `arquivo` guardam o objeto retornado pelo upload localmente
        // (para exibir nome/tamanho), mas o servidor só aceita o id do upload.
        const valoresEnvio: Record<string, any> = {};
        for (const [chave, valor] of Object.entries(valores)) {
            const campo = definicao.campos.find((c) => c.nome === chave);
            valoresEnvio[chave] = campo?.tipo === 'arquivo' && valor ? valor.id : valor;
        }

        setEnviando(true);
        try {
            const res = await axios.post(`/api/formulario-publico/${token}`, { valores: valoresEnvio });
            setVoltarUrl(res.data?.voltarUrl ?? null);
            setEstado('preenchido');
        } catch (err: any) {
            const data = err.response?.data;
            if (data?.estado === 'invalido' && data.erros) {
                setErros(data.erros);
            } else if (data?.estado === 'expirado') {
                setEstado('expirado');
            } else if (data?.estado === 'preenchido') {
                setEstado('preenchido');
            } else {
                setEstado('erro');
            }
        } finally {
            setEnviando(false);
        }
    };

    const handleCancelar = async () => {
        setCancelando(true);
        try {
            const res = await axios.post(`/api/formulario-publico/${token}/cancelar`);
            setVoltarUrl(res.data?.voltarUrl ?? null);
            setEstado('cancelado');
        } catch (err: any) {
            // O desfecho já é outro (preenchido/expirado): mostra a tela dele em
            // vez de um erro genérico.
            const data = err.response?.data;
            setEstado(
                data?.estado === 'preenchido' || data?.estado === 'expirado' ? data.estado : 'erro'
            );
        } finally {
            setCancelando(false);
        }
    };

    const campoObrigatorio = (campo: MiniFormularioCampo) =>
        (campo.validadores ?? []).some((v) => v.tipo === 'obrigatorio');

    // Os componentes de campo que recebem `error` já desenham a mensagem (e
    // pintam a borda). O <span> abaixo do campo só existe para os que não
    // recebem: input cru, checkbox, radio, múltipla escolha e agendamento.
    // Sem esta distinção o mesmo erro aparece duas vezes.
    const campoDesenhaProprioErro = (campo: MiniFormularioCampo) => {
        if (campo.tipo === 'lista_opcoes') return campo.estilo_lista !== 'radio';
        return ['telefone', 'cpf', 'cnpj', 'date', 'datetime', 'arquivo', 'carrinho'].includes(
            campo.tipo
        );
    };

    const renderCampo = (campo: MiniFormularioCampo) => {
        const erro = erros[campo.nome];
        const inputClass = `${styles.input} ${erro ? styles.inputErro : ''}`;
        const valor = valores[campo.nome] ?? '';

        if (campo.tipo === 'agendamento') {
            return (
                <CampoAgendamento
                    token={token!}
                    campoNome={campo.nome}
                    value={valores[campo.nome] || undefined}
                    onSelect={(datetime, slotId) => {
                        setValor(campo.nome, datetime);
                        setValor(`${campo.nome}_slotId`, slotId);
                    }}
                />
            );
        }

        if (campo.tipo === 'carrinho') {
            return (
                <CampoCarrinho
                    token={token!}
                    campoNome={campo.nome}
                    maxItens={campo.carrinho?.max_itens}
                    value={valores[campo.nome] || undefined}
                    onChange={(itens) => setValor(campo.nome, itens)}
                    error={erro}
                />
            );
        }

        if (campo.tipo === 'arquivo') {
            return (
                <CampoArquivo
                    token={token!}
                    campoNome={campo.nome}
                    filtro={campo.filtro_arquivo}
                    value={valores[campo.nome] || undefined}
                    onChange={(arquivo) => setValor(campo.nome, arquivo)}
                    error={erro}
                />
            );
        }

        if (campo.tipo === 'lista_opcoes') {
            const opcoes = campo.opcoes ?? [];

            if (campo.estilo_lista === 'radio') {
                return (
                    <div className={styles.opcoesLista}>
                        {opcoes.map((opcao) => (
                            <CampoRadio
                                key={opcao}
                                id={`${campo.nome}-${opcao}`}
                                name={campo.nome}
                                label={opcao}
                                value={opcao}
                                checked={valores[campo.nome] === opcao}
                                onChange={() => setValor(campo.nome, opcao)}
                            />
                        ))}
                    </div>
                );
            }

            return (
                <CampoSeletor
                    instanceId={campo.nome}
                    options={opcoes.map((o) => ({ id: o, label: o }))}
                    value={valores[campo.nome] ?? null}
                    onChange={(v) => setValor(campo.nome, v)}
                    placeholder={campo.placeholder}
                    error={erro}
                />
            );
        }

        if (campo.tipo === 'multipla_escolha') {
            const opcoes = campo.opcoes ?? [];
            const selecionadas: string[] = Array.isArray(valores[campo.nome]) ? valores[campo.nome] : [];

            return (
                <div className={styles.opcoesLista}>
                    {opcoes.map((opcao) => (
                        <CampoCheckbox
                            key={opcao}
                            instanceId={`${campo.nome}-${opcao}`}
                            value={selecionadas.includes(opcao)}
                            onChange={(marcado) =>
                                setValor(
                                    campo.nome,
                                    marcado
                                        ? [...selecionadas, opcao]
                                        : selecionadas.filter((v) => v !== opcao)
                                )
                            }
                        >
                            {opcao}
                        </CampoCheckbox>
                    ))}
                </div>
            );
        }

        if (campo.tipo === 'boolean') {
            return (
                <div className={styles.checkboxLinha}>
                    <input
                        id={campo.nome}
                        type="checkbox"
                        checked={!!valores[campo.nome]}
                        onChange={(e) => setValor(campo.nome, e.target.checked)}
                    />
                    <label htmlFor={campo.nome}>
                        {campo.placeholder || campo.label}
                        {campoObrigatorio(campo) && <span className={styles.obrigatorio}>*</span>}
                    </label>
                </div>
            );
        }

        if (campo.tipo === 'date') {
            return (
                <CampoData
                    id={campo.nome}
                    type="date"
                    placeholder={campo.placeholder ?? ''}
                    value={valores[campo.nome] || ''}
                    onChange={(v) => setValor(campo.nome, v)}
                    error={erro}
                />
            );
        }


        if (campo.tipo === 'datetime') {
            return (
                <CampoData
                    id={campo.nome}
                    type="datetime-local"
                    placeholder={campo.placeholder ?? ''}
                    value={valores[campo.nome] || ''}
                    onChange={(v) => setValor(campo.nome, v)}
                    error={erro}
                />
            );
        }

        if (campo.tipo === 'telefone') {
            return (
                <CampoTelefone
                    instanceId={campo.nome}
                    placeholder={campo.placeholder ?? ''}
                    value={valor}
                    onChange={(v) => setValor(campo.nome, v)}
                    error={erro}
                />
            );
        }

        if (campo.tipo === 'cpf') {
            return (
                <CampoMascara
                    masks={['999.999.999-99']}
                    placeholder={campo.placeholder ?? ''}
                    value={valor}
                    onChange={(v) => setValor(campo.nome, v)}
                    error={erro}
                />
            );
        }

        if (campo.tipo === 'cnpj') {
            return (
                <CampoMascara
                    masks={['99.999.999/9999-99']}
                    placeholder={campo.placeholder ?? ''}
                    value={valor}
                    onChange={(v) => setValor(campo.nome, v)}
                    error={erro}
                />
            );
        }

        const tipoInput =
            campo.tipo === 'number' ? 'number'
                : campo.tipo === 'email' ? 'email'
                : 'text';

        return (
            <input
                id={campo.nome}
                className={inputClass}
                type={tipoInput}
                placeholder={campo.placeholder ?? ''}
                value={valor}
                onChange={(e) => setValor(campo.nome, e.target.value)}
            />
        );
    };

    const renderMensagem = (titulo: string, texto: string) => (
        <div className={styles.card}>
            <div className={styles.centro}>
                <h1 className={styles.titulo}>{titulo}</h1>
                <p className={styles.descricao}>
                    {texto ? texto.split('\n').map((linha, i) => <div key={i}>{linha}</div>) : null}
                </p>
                {voltarUrl && (
                    <a className={styles.voltarLink} href={voltarUrl}>
                        Voltar à conversa
                    </a>
                )}
            </div>
        </div>
    );

    let conteudo: React.ReactNode;
    if (estado === 'carregando') {
        conteudo = renderMensagem('Carregando...', 'Aguarde um instante.');
    } else if (estado === 'preenchido') {
        conteudo = renderMensagem('Tudo certo!', 'Recebemos suas respostas.\nVocê já pode fechar esta janela e voltar à conversa.');
    } else if (estado === 'cancelado') {
        conteudo = renderMensagem(
            'Formulário cancelado',
            'Tudo bem, você não precisa preencher agora.\nVocê já pode fechar esta janela e voltar à conversa.'
        );
    } else if (estado === 'expirado') {
        conteudo = renderMensagem('Formulário expirado', 'O prazo para preencher este formulário terminou.');
    } else if (estado === 'invalido') {
        conteudo = renderMensagem('Formulário não encontrado', 'Este link é inválido ou já foi utilizado.');
    } else if (estado === 'erro') {
        conteudo = renderMensagem('Ops!', 'Ocorreu um erro. Tente novamente mais tarde.');
    } else if (estado === 'pendente' && definicao) {
        conteudo = (
            <form className={styles.card} onSubmit={handleSubmit}>
                <h1 className={styles.titulo}>{definicao.nome}</h1>
                {definicao.descricao && <p className={styles.descricao}>{definicao.descricao}</p>}
                {definicao.campos.some(campoObrigatorio) && (
                    <p className={styles.descricao}>
                        <span className={styles.obrigatorio}>*</span> Campos obrigatórios
                    </p>
                )}

                {definicao.campos.map((campo) => (
                    <div className={styles.campo} key={campo.nome}>
                        {campo.tipo !== 'boolean' && (
                            <label className={styles.label} htmlFor={campo.nome}>
                                {campo.label}
                                {campoObrigatorio(campo) && <span className={styles.obrigatorio}>*</span>}
                            </label>
                        )}
                        {renderCampo(campo)}
                        {campo.ajuda && <span className={styles.ajuda}>{campo.ajuda}</span>}
                        {erros[campo.nome] && !campoDesenhaProprioErro(campo) && (
                            <span className={styles.erro}>{erros[campo.nome]}</span>
                        )}
                    </div>
                ))}

                {erros[CHAVE_FORMULARIO] && (
                    <div className={styles.erroGeral}>{erros[CHAVE_FORMULARIO]}</div>
                )}

                <button className={styles.botao} type="submit" disabled={enviando || cancelando}>
                    {enviando ? 'Enviando...' : (definicao.texto_botao || 'Enviar')}
                </button>

                {cancelarLabel && (
                    <button
                        className={styles.botaoSecundario}
                        type="button"
                        disabled={enviando || cancelando}
                        onClick={handleCancelar}
                    >
                        {cancelando ? 'Cancelando...' : cancelarLabel}
                    </button>
                )}
            </form>
        );
    }

    return <div className={styles.wrapper}>{conteudo}</div>;
};

export default FormularioPublico;
